import { parseAsRFCSafeString } from '@/hooks/use-table-filter/utils';
import type { Folder, Bookmark } from '@/lib/bookmark/types';
import { workflowPresetColors } from '@/lib/colors';
import {
  CURRENT_USER_FILTER_SENTINEL,
  LAST_2_WEEKS_FILTER_SENTINEL,
} from '@/requests/hooks/filter-rewrites';

export interface WorkflowPresetBookmark extends Omit<
  Bookmark,
  'client_id' | 'sub_kind' | 'icon'
> {
  readonly sub_kind?: Bookmark['sub_kind'];
  readonly icon?: Bookmark['icon'];
}

export interface WorkflowPreset {
  readonly presetId: string;
  name: string;
  description: string;
  kind: string;
  sub_kind?: string | null;
  icon?: string | null;
  color: string | null;
  order: number;
  pinned: boolean;
  child_folders: Folder[];
  child_bookmarks: WorkflowPresetBookmark[];
}

export const presets: WorkflowPreset[] = [
  {
    presetId: 'my-errands-contractor',
    name: 'Mina ärenden (utförare)',
    description: 'Ärenden där användaren är ansvarig utförare.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Obesvarade',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22status%22%3A%7B%22__in!%22%3A%5B%226%2C4%2C5%22%2C%226%2C4%2C5%22%5D%7D%2C%22users_unanswered_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva (ej planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22true%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva (planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Planerade (kommande 7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 4,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%2C%22__date_time_future%22%3A%227%22%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Försenade',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 5,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22custom_generalerrand_filter%22%3A%7B%22noop%22%3A%5B%22delayed%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'my-errands-case-handler',
    name: 'Mina ärenden (handläggare / underärenden)',
    description:
      'Ärenden där användaren är handläggare eller ansvarar för underärenden.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Aktiva',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 0,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva underärenden',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22is_errand_group_initiator%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%2C%22errand_group%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Ej planerade underärenden',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22is_errand_group_initiator%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%2C%22errand_group%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22true%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Planerade underärenden kommande 7 dagar',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22is_errand_group_initiator%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%2C%22errand_group%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__date_time_future%22%3A%227%22%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Försenade underärenden',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: `%7B%22users_accepted_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22is_errand_group_initiator%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%2C%22errand_group%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%2C%22custom_generalerrand_filter%22%3A%7B%22noop%22%3A%5B%22delayed%22%5D%7D%7D`,
          model_name: 'generalerrand',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'my-inspections-planner',
    name: 'Mina besiktningar (planeringsansvarig)',
    description: 'Besiktningar som användaren planerar och schemalägger.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Obesvarade',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_unanswered_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in!%22%3A%5B%225%2C4%22%5D%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva (ej planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_accepted_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22true%22%5D%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva (planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_accepted_delegations_planner%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Utförda senaste 2 veckorna (av alla)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 3,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter:
            '%7B%22actual_done_time%22%3A%7B%22__date_past%22%3A%2214%22%7D%7D',
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'my-inspections-contractor',
    name: 'Mina besiktningar (utförare)',
    description: 'Besiktningar som användaren ska genomföra.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Obesvarade',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_unanswered_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in!%22%3A%5B%225%2C4%22%5D%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva (ej planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22true%22%5D%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Aktiva (planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Planerade (kommande 7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 4,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%2C%22__date_future%22%3A%227%22%7D%7D`,
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'my-rounds-contractor',
    name: 'Mina Ronder (Utförare)',
    description: 'Ronder som användaren ska genomföra.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Obesvarade',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/technical-management/rounds',
        query_params: {
          filter: `%7B%22users_unanswered_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in!%22%3A%5B%224%22%5D%7D%7D`,
          model_name: 'roundingoccurrence',
          _search: '',
        },
      },
      {
        name: 'Aktiva',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/technical-management/rounds',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%7D`,
          model_name: 'roundingoccurrence',
          _search: '',
        },
      },
      {
        name: 'Aktiva (ej planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/technical-management/rounds',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22true%22%5D%7D%7D`,
          model_name: 'roundingoccurrence',
          _search: '',
        },
      },
      {
        name: 'Aktiva (planerade)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/technical-management/rounds',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%7D%7D`,
          model_name: 'roundingoccurrence',
          _search: '',
        },
      },
      {
        name: 'Planerade (kommande 7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 4,
        pinned: true,
        path: '/dashboard/technical-management/rounds',
        query_params: {
          filter: `%7B%22users_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22status%22%3A%7B%22__in%22%3A%5B%221%2C2%22%5D%7D%2C%22planned_done_time%22%3A%7B%22__isnull%22%3A%5B%22false%22%5D%2C%22__date_future%22%3A%227%22%7D%7D`,
          model_name: 'roundingoccurrence',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'all-errands',
    name: 'Alla ärenden',
    description: 'Samlad översikt över alla ärenden i systemet.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Skapades (senaste 30 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 0,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter:
            '%7B%22created_at%22%3A%7B%22__date_time_past%22%3A%2230%22%7D%7D',
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Ej delegerade',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 1,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter:
            '%7B%22has_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%2C%22has_open_delegations%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%7D',
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Ej accepterade',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 2,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22noop%22%3A%5B%220%22%5D%7D%2C%22has_open_delegations%22%3A%7B%22noop%22%3Atrue%7D%7D',
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Accepterade',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: '%7B%22status%22%3A%7B%22noop%22%3A%5B%221%22%5D%7D%7D',
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Påbörjade',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 4,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter: '%7B%22status%22%3A%7B%22noop%22%3A%5B%222%22%5D%7D%7D',
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Försenade',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 5,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter:
            '%7B%22custom_generalerrand_filter%22%3A%7B%22noop%22%3A%5B%22delayed%22%5D%7D%7D',
          model_name: 'generalerrand',
          _search: '',
        },
      },
      {
        name: 'Avslutade (senaste 30 dagarna)',
        kind: 'workflow',
        color: null,
        order: 6,
        pinned: true,
        path: '/dashboard/technical-management/errands',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%226%2C4%2C5%22%5D%7D%2C%22actual_done_time%22%3A%7B%22__date_time_past%22%3A%2230%22%7D%7D',
          model_name: 'generalerrand',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'all-inspections',
    name: 'Alla besiktningar',
    description: 'Samlad översikt över alla besiktningar.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Skapades (senaste 30 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 0,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter:
            '%7B%22created_at%22%3A%7B%22__date_time_past%22%3A%2230%22%7D%7D',
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Ej delegerade',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 1,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter:
            '%7B%22has_accepted_delegations%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%2C%22has_open_delegations%22%3A%7B%22noop%22%3A%5B%22false%22%5D%7D%7D',
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Ej accepterade',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 2,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter:
            '%7B%22has_open_delegations%22%3A%7B%22noop%22%3Atrue%7D%2C%22status%22%3A%7B%22noop%22%3A%5B%220%22%5D%7D%7D',
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Accepterade',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: '%7B%22status%22%3A%7B%22noop%22%3A%5B%221%22%5D%7D%7D',
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Påbörjade',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 4,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter: '%7B%22status%22%3A%7B%22noop%22%3A%5B%222%22%5D%7D%7D',
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
      {
        name: 'Avslutade (senaste 30 dagarna)',
        kind: 'workflow',
        color: null,
        order: 5,
        pinned: true,
        path: '/dashboard/technical-management/inspections',
        query_params: {
          filter:
            '%7B%22actual_done_time%22%3A%7B%22__date_past%22%3A%2230%22%7D%7D',
          model_name: 'inspectionerrand',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'new-apartment-contracts',
    name: 'Nya lägenhetsavtal',
    description: 'Skapa och registrera nya hyresavtal för lägenheter.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Utkast',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/apartment/drafts',
        query_params: {
          filter: '%7B%22is_draft%22%3A%7B%22__in%22%3Atrue%7D%7D',
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Skarpa avtal (ännu ej signerade)',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%2210%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Pågående signeringar',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 39,
        pinned: true,
        path: '/dashboard/digital-signing/processes',
        query_params: {
          filter: `%7B%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.apartmentcontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
          model_name: 'signabledocument',
          override_url: 'contracts.apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Klara signeringar (senaste 14 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter:
            '%7B%22signed_time%22%3A%7B%22__date_past%22%3A%2214%22%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Nekade signeringar',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/digital-signing/processes',
        query_params: {
          filter: `%7B%22custom_signable_doc_filter%22%3A%7B%22noop%22%3A%5B%22rejected_last_2_weeks%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.apartmentcontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
          model_name: 'signabledocument',
          override_url: 'contracts.apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Avbrutna signeringar',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 5,
        pinned: true,
        path: '/dashboard/digital-signing/processes',
        query_params: {
          filter: `%7B%22custom_signable_doc_filter%22%3A%7B%22noop%22%3A%5B%22aborted_last_2_weeks%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.apartmentcontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
          model_name: 'signabledocument',
          override_url: 'contracts.apartmentcontract',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'apartment-lease-terminations',
    name: 'Uppsägningar lägenheter',
    description: 'Hantera uppsägningar av lägenhetsavtal.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Uppsägningsprocesser',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%224%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Uppsagda avtal',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%226%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Nuvarande och kommande vakanser',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 2,
        pinned: true,
        path: '/dashboard/apartments',
        query_params: {
          filter:
            '%7B%22custom_space_status_filter%22%3A%7B%22noop%22%3A%5B%22vacant_no_future_contract%22%5D%7D%7D',
          model_name: 'apartment',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'apartments-move-in-out',
    name: 'Lägenheter: in/ut-flytt',
    description: 'Hantera processer vid in- och utflytt för lägenheter.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Inflytt (90 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: {
              __in: ['9'],
            },
            is_draft: {
              __in: false,
            },
            start_date: {
              __date_future: '90',
            },
          }),
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Inflytt (30 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: {
              __in: ['9'],
            },
            is_draft: {
              __in: false,
            },
            start_date: {
              __date_future: '30',
            },
          }),
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Inflytt (7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: {
              __in: ['9'],
            },
            is_draft: {
              __in: false,
            },
            start_date: {
              __date_future: '7',
            },
          }),
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt (90 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 3,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6'],
            },
            lifetime_end_date: {
              __date_future: '90',
            },
          }),
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt (30 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6'],
            },
            lifetime_end_date: {
              __date_future: '30',
            },
          }),
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt (7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 5,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6'],
            },
            lifetime_end_date: {
              __date_future: '7',
            },
          }),
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'new-premises-contracts',
    name: 'Nya lokalavtal',
    description: 'Skapa och registrera nya hyresavtal för lokaler.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Utkast',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises/drafts',
        query_params: {
          filter: '%7B%22is_draft%22%3A%7B%22__in%22%3Atrue%7D%7D',
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Skarpa avtal (ännu ej signerade)',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%2210%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Klara avtal (senaste 14 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter:
            '%7B%22signed_time%22%3A%7B%22__date_past%22%3A%2214%22%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      // TODO: Add back when cancellation branch is merged and we have new separate paths for each contract type
      // {
      //   name: 'Pågående signeringar',
      //   kind: 'workflow',
      //   color: workflowPresetColors.infoAlt,
      //   order: 2,
      //   pinned: true,
      //   path: '/dashboard/digital-signing/processes',
      //   query_params: {
      //     filter: `%7B%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.industrialpremisescontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
      //     model_name: 'signabledocument',
      //     override_url: 'contracts.industrialpremisescontract',
      //     _search: '',
      //   },
      // },
      // {
      //   name: 'Nekade signeringar',
      //   kind: 'workflow',
      //   color: workflowPresetColors.info,
      //   order: 4,
      //   pinned: true,
      //   path: '/dashboard/digital-signing/processes',
      //   query_params: {
      //     filter: `%7B%22custom_signable_doc_filter%22%3A%7B%22noop%22%3A%5B%22rejected_last_2_weeks%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.industrialpremisescontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
      //     model_name: 'signabledocument',
      //     override_url: 'contracts.industrialpremisescontract',
      //     _search: '',
      //   },
      // },
      // {
      //   name: 'Avbrutna signeringar',
      //   kind: 'workflow',
      //   color: workflowPresetColors.info,
      //   order: 5,
      //   pinned: true,
      //   path: '/dashboard/digital-signing/processes',
      //   query_params: {
      //     filter: `%7B%22custom_signable_doc_filter%22%3A%7B%22noop%22%3A%5B%22aborted_last_2_weeks%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.industrialpremisescontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
      //     model_name: 'signabledocument',
      //     override_url: 'contracts.industrialpremisescontract',
      //     _search: '',
      //   },
      // },
    ],
  },
  {
    presetId: 'premises-lease-terminations',
    name: 'Uppsägningar lokaler',
    description: 'Hantera uppsägningar av lokalhyresavtal.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Uppsägningsprocesser',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%224%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Uppsagda avtal',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%226%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Omförhandlingsprocesser',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 2,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%223%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Uppsagda för omförhandling',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 3,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%225%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Nuvarande och kommande vakanser',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/industrial-premises',
        query_params: {
          filter:
            '%7B%22custom_space_status_filter%22%3A%7B%22noop%22%3A%5B%22vacant_no_future_contract%22%5D%7D%7D',
          model_name: 'industrialpremises',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'premises-move-in-out',
    name: 'Lokaler: in/ut-flytt',
    description: 'Hantera processer vid in- och utflytt för lokaler.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Inflytt (90 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: {
              __in: ['9'],
            },
            start_date: {
              __date_future: '90',
            },
            is_draft: {
              __in: false,
            },
          }),
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Inflytt (30 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: {
              __in: ['9'],
            },
            start_date: {
              __date_future: '30',
            },
            is_draft: {
              __in: false,
            },
          }),
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Inflytt (7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: {
              __in: ['9'],
            },
            start_date: {
              __date_future: '7',
            },
            is_draft: {
              __in: false,
            },
          }),
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt, ink omförhandling (90 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 3,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6', '5'],
            },
            lifetime_end_date: {
              __date_future: '90',
            },
          }),
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt, ink. omförhandling (30 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6', '5'],
            },
            lifetime_end_date: {
              __date_future: '30',
            },
          }),
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt, ink. omförhandling (7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 5,
        pinned: true,
        path: '/dashboard/contracts/industrial-premises',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6', '5'],
            },
            lifetime_end_date: {
              __date_future: '7',
            },
          }),
          model_name: 'industrialpremisescontract',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'new-parking-contracts',
    name: 'Nya parkeringsavtal',
    description: 'Skapa och registrera nya parkeringsavtal.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Utkast',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/parking-spot/drafts',
        query_params: {
          filter: '%7B%22is_draft%22%3A%7B%22__in%22%3Atrue%7D%7D',
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Skarpa avtal (ännu ej signerade)',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%2210%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Klara avtal (senaste 14 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter:
            '%7B%22signed_time%22%3A%7B%22__date_past%22%3A%2214%22%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      // TODO: Add back when cancellation branch is merged and we have new separate paths for each contract type
      // {
      //   name: 'Pågående signeringar',
      //   kind: 'workflow',
      //   color: workflowPresetColors.infoAlt,
      //   order: 2,
      //   pinned: true,
      //   path: '/dashboard/digital-signing/processes',
      //   query_params: {
      //     filter: `%7B%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.parkingspotcontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
      //     model_name: 'signabledocument',
      //     override_url: 'contracts.parkingspotcontract',
      //     _search: '',
      //   },
      // },
      // {
      //   name: 'Nekade signeringar',
      //   kind: 'workflow',
      //   color: workflowPresetColors.info,
      //   order: 4,
      //   pinned: true,
      //   path: '/dashboard/digital-signing/processes',
      //   query_params: {
      //     filter: `%7B%22custom_signable_doc_filter%22%3A%7B%22noop%22%3A%5B%22rejected_last_2_weeks%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.parkingspotcontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
      //     model_name: 'signabledocument',
      //     override_url: 'contracts.parkingspotcontract',
      //     _search: '',
      //   },
      // },
      // {
      //   name: 'Avbrutna signeringar',
      //   kind: 'workflow',
      //   color: workflowPresetColors.info,
      //   order: 5,
      //   pinned: true,
      //   path: '/dashboard/digital-signing/processes',
      //   query_params: {
      //     filter: `%7B%22custom_signable_doc_filter%22%3A%7B%22noop%22%3A%5B%22aborted_last_2_weeks%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%2C%22signed_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22signing_aborted_time%22%3A%7B%22__isnull%22%3Atrue%7D%2C%22rejected_time%22%3A%7B%22__lte!%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22content_type%22%3A%7B%22noop%22%3A%22contracts.parkingspotcontract%22%7D%2C%22attached_to%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
      //     model_name: 'signabledocument',
      //     override_url: 'contracts.parkingspotcontract',
      //     _search: '',
      //   },
      // },
    ],
  },
  {
    presetId: 'parking-lease-terminations',
    name: 'Uppsägningar parkeringar',
    description: 'Hantera uppsägningar av parkeringsavtal.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Uppsägningsprocesser',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%224%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Uppsagda avtal',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%226%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Nuvarande och kommande vakanser',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 2,
        pinned: true,
        path: '/dashboard/parking-spots',
        query_params: {
          filter:
            '%7B%22custom_space_status_filter%22%3A%7B%22noop%22%3A%5B%22vacant_no_future_contract%22%5D%7D%7D',
          model_name: 'parkingspot',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'parking-move-in-out',
    name: 'Parkeringar: in/ut-flytt',
    description: 'Hantera tilldelning och avslut av parkeringsplatser.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Inflytt (90 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            start_date: {
              __date_future: '90',
            },
            status: {
              __in: ['9'],
            },
            is_draft: {
              __in: false,
            },
          }),
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Inflytt (30 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            start_date: {
              __date_future: '30',
            },
            status: {
              __in: ['9'],
            },
            is_draft: {
              __in: false,
            },
          }),
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Inflytt (7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 2,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            start_date: {
              __date_future: '7',
            },
            status: {
              __in: ['9'],
            },
            is_draft: {
              __in: false,
            },
          }),
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt (90 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 3,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6'],
            },
            lifetime_end_date: {
              __date_future: '90',
            },
          }),
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt (30 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6'],
            },
            lifetime_end_date: {
              __date_future: '30',
            },
          }),
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
      {
        name: 'Utflytt (7 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 5,
        pinned: true,
        path: '/dashboard/contracts/parking-spot',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            is_draft: {
              __in: false,
            },
            status: {
              __in: ['6'],
            },
            lifetime_end_date: {
              __date_future: '7',
            },
          }),
          model_name: 'parkingspotcontract',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'rent-notices',
    name: 'Avisering',
    description: 'Hantera skapande och uppföljning av hyresavier.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Ej attesterade',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/invoicing/invoices',
        query_params: {
          filter:
            '%7B%22custom_invoice_filter%22%3A%7B%22noop%22%3A%5B%22not_attested%22%5D%7D%2C%22rental_invoice%22%3A%7B%22noop%22%3Atrue%7D%7D',
          model_name: 'invoice',
          _search: '',
        },
      },
      {
        name: 'Öppna avier',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 1,
        pinned: true,
        path: '/dashboard/invoicing/invoices',
        query_params: {
          filter:
            '%7B%22custom_invoice_filter%22%3A%7B%22noop%22%3A%5B%22open%22%5D%7D%2C%22rental_invoice%22%3A%7B%22noop%22%3Atrue%7D%7D',
          model_name: 'invoice',
          _search: '',
        },
      },
      {
        name: 'Misslyckade leveranser',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 2,
        pinned: true,
        path: '/dashboard/invoicing/failed-deliveries',
        query_params: {
          filter: '%7B%7D',
          model_name: 'unset',
          _search: '',
        },
      },
      {
        name: 'Förfallna',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 3,
        pinned: true,
        path: '/dashboard/invoicing/invoices',
        query_params: {
          filter:
            '%7B%22custom_invoice_filter%22%3A%7B%22noop%22%3A%5B%22overdue%22%5D%7D%2C%22rental_invoice%22%3A%7B%22noop%22%3Atrue%7D%7D',
          model_name: 'invoice',
          _search: '',
        },
      },
      {
        name: 'Kravhantering',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/invoicing/invoices',
        query_params: {
          filter:
            '%7B%22custom_invoice_filter%22%3A%7B%22noop%22%3A%5B%22debt%22%5D%7D%2C%22rental_invoice%22%3A%7B%22noop%22%3Atrue%7D%7D',
          model_name: 'invoice',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'payments',
    name: 'Betalningar',
    description: 'Hantera och följa upp betalningar.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Inkomna och matchade (senaste 30 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 1,
        pinned: true,
        path: '/dashboard/invoicing/payments',
        query_params: {
          filter:
            '%7B%22custom_payments_filter%22%3A%7B%22noop%22%3A%5B%22distributed%22%5D%7D%2C%22created_at%22%3A%7B%22__date_time_past%22%3A%2230%22%7D%7D',
          model_name: 'invoicepayment',
          _search: '',
        },
      },
      {
        name: 'Inkomna och borttagna (senaste 30 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 2,
        pinned: true,
        path: '/dashboard/invoicing/payments',
        query_params: {
          filter:
            '%7B%22created_at%22%3A%7B%22__date_time_past%22%3A%2230%22%7D%2C%22custom_payments_filter%22%3A%7B%22noop%22%3A%5B%22annulled%22%5D%7D%7D',
          model_name: 'invoicepayment',
          _search: '',
        },
      },
      {
        name: 'Ej hanterade',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 3,
        pinned: true,
        path: '/dashboard/invoicing/payments',
        query_params: {
          filter:
            '%7B%22custom_payments_filter%22%3A%7B%22noop%22%3A%5B%22not_handled%22%5D%7D%7D',
          model_name: 'invoicepayment',
          _search: '',
        },
      },
      {
        name: 'Ej matchade',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/invoicing/payments',
        query_params: {
          filter:
            '%7B%22custom_payments_filter%22%3A%7B%22noop%22%3A%5B%22not_matched%22%5D%7D%7D',
          model_name: 'invoicepayment',
          _search: '',
        },
      },
      {
        name: 'Överbetalningar',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 5,
        pinned: true,
        path: '/dashboard/invoicing/payments',
        query_params: {
          filter:
            '%7B%22custom_payments_filter%22%3A%7B%22noop%22%3A%5B%22overpayment%22%5D%7D%7D',
          model_name: 'invoicepayment',
          _search: '',
        },
      },
      {
        name: 'Misslyckade autogiro',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 6,
        pinned: true,
        path: '/dashboard/invoicing/failed-autogiro-transaction-attempts',
        query_params: {
          filter:
            '%7B%22autogiro_enabled%22%3A%7B%22noop%22%3Atrue%7D%2C%22has_failed_autogiro_payment%22%3A%7B%22noop%22%3Atrue%7D%2C%22has_successful_autogiro_payment%22%3A%7B%22noop%22%3Afalse%7D%2C%22closed_time%22%3A%7B%22__isnull%22%3Atrue%7D%7D',
          model_name: 'invoice',
          _search: '',
        },
      },
      {
        name: 'Väntande autogiro',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 7,
        pinned: true,
        path: '/dashboard/invoicing/pending-autogiro-transaction-attempts',
        query_params: {
          filter: `%7B%22autogiro_enabled%22%3A%7B%22noop%22%3Atrue%7D%2C%22has_failed_autogiro_payment%22%3A%7B%22noop%22%3Afalse%7D%2C%22has_successful_autogiro_payment%22%3A%7B%22noop%22%3Afalse%7D%2C%22has_delivered_autogiro_payment%22%3A%7B%22noop%22%3Atrue%7D%2C%22attested_date%22%3A%7B%22__isnull%22%3Afalse%7D%2C%22due_date%22%3A%7B%22__gte%22%3A%22${LAST_2_WEEKS_FILTER_SENTINEL}%22%7D%2C%22closed_time%22%3A%7B%22__isnull%22%3Atrue%7D%7D`,
          model_name: 'invoice',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'late-paying-tenants',
    name: 'Sena hyresgäster',
    description:
      'Identifiera och hantera hyresgäster med försenade betalningar.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Minst 1 förfallen fodring',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 0,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%220%2C1%2C2%2C3%2C4%2C5%2C7%2C8%22%5D%7D%2C%22overdue_invoices_count%22%3A%7B%22__gte%22%3A%221%22%7D%2C%22show_in_brf_context%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 3 förfallna fodringar',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 1,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%220%2C1%2C2%2C3%2C4%2C5%2C7%2C8%22%5D%7D%2C%22overdue_invoices_count%22%3A%7B%22__gte%22%3A%223%22%7D%2C%22show_in_brf_context%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 5 förfallna fodringar',
        kind: 'workflow',
        color: workflowPresetColors.critical,
        order: 2,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%220%2C1%2C2%2C3%2C4%2C5%2C7%2C8%22%5D%7D%2C%22overdue_invoices_count%22%3A%7B%22__gte%22%3A%225%22%7D%2C%22show_in_brf_context%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 1 inkassoärende',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 3,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%220%2C1%2C2%2C3%2C4%2C5%2C7%2C8%22%5D%7D%2C%22debt_collection_invoices_count%22%3A%7B%22__gte%22%3A%221%22%7D%2C%22show_in_brf_context%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 3 inkassoärenden',
        kind: 'workflow',
        color: workflowPresetColors.info,
        order: 4,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%220%2C1%2C2%2C3%2C4%2C5%2C7%2C8%22%5D%7D%2C%22debt_collection_invoices_count%22%3A%7B%22__gte%22%3A%223%22%7D%2C%22show_in_brf_context%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 3 sent betalda fodringar',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 5,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%220%2C1%2C2%2C3%2C4%2C5%2C7%2C8%22%5D%7D%2C%22late_paid_invoices_count%22%3A%7B%22__gte%22%3A%223%22%7D%2C%22show_in_brf_context%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 5 sent betalda fordringar',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 6,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter:
            '%7B%22status%22%3A%7B%22__in%22%3A%5B%220%2C1%2C2%2C3%2C4%2C5%2C7%2C8%22%5D%7D%2C%22late_paid_invoices_count%22%3A%7B%22__gte%22%3A%225%22%7D%2C%22show_in_brf_context%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 3 sent betalda (6 mån)',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 7,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: { __in: ['0,1,2,3,4,5,7,8'] },
            late_paid_invoices_count: { __gte: '3' },
            late_paid_invoices_time_window: { noop: ['6m'] },
            show_in_brf_context: { noop: false },
          }),
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 3 sent betalda (1 år)',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 8,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: { __in: ['0,1,2,3,4,5,7,8'] },
            late_paid_invoices_count: { __gte: '3' },
            late_paid_invoices_time_window: { noop: ['1y'] },
            show_in_brf_context: { noop: false },
          }),
          model_name: 'tenant',
          _search: '',
        },
      },
      {
        name: 'Minst 3 sent betalda (2 år)',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 9,
        pinned: true,
        path: '/dashboard/tenants',
        query_params: {
          filter: parseAsRFCSafeString.serialize({
            status: { __in: ['0,1,2,3,4,5,7,8'] },
            late_paid_invoices_count: { __gte: '3' },
            late_paid_invoices_time_window: { noop: ['2y'] },
            show_in_brf_context: { noop: false },
          }),
          model_name: 'tenant',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'contact-persons',
    name: 'Kontaktpersoner (andrahandshyresgäster mm.) ',
    description: 'Register över externa kontaktpersoner kopplade till avtal.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Andrahandsuthyrda lägenhetsavtal',
        kind: 'workflow',
        color: null,
        order: 0,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter:
            '%7B%22custom_contract_filter%22%3A%7B%22noop%22%3A%5B%22sub_lease_active%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Lägenhetsavtal med borgensman',
        kind: 'workflow',
        color: null,
        order: 1,
        pinned: true,
        path: '/dashboard/contracts/apartment',
        query_params: {
          filter:
            '%7B%22custom_contract_filter%22%3A%7B%22noop%22%3A%5B%22guarantor_active%22%5D%7D%2C%22is_draft%22%3A%7B%22__in%22%3Afalse%7D%7D',
          model_name: 'apartmentcontract',
          _search: '',
        },
      },
      {
        name: 'Koppling löper ut (30 dagar) ',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 2,
        pinned: true,
        path: '/dashboard/sub-tenants',
        query_params: {
          filter:
            '%7B%22latest_active_date%22%3A%7B%22__date_future%22%3A%2230%22%7D%7D',
          model_name: 'subtenant',
          _search: '',
        },
      },
      {
        name: 'Koppling löper ut (90 dagar)',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 3,
        pinned: true,
        path: '/dashboard/sub-tenants',
        query_params: {
          filter:
            '%7B%22latest_active_date%22%3A%7B%22__date_future%22%3A%2290%22%7D%7D',
          model_name: 'subtenant',
          _search: '',
        },
      },
      {
        name: 'Koppling löper ut (1 år)',
        kind: 'workflow',
        color: workflowPresetColors.warning,
        order: 4,
        pinned: true,
        path: '/dashboard/sub-tenants',
        query_params: {
          filter:
            '%7B%22latest_active_date%22%3A%7B%22__date_future%22%3A%22365%22%7D%7D',
          model_name: 'subtenant',
          _search: '',
        },
      },
    ],
  },
  {
    presetId: 'send-outs',
    name: 'Utskick',
    description: 'Hantera massutskick och kommunikation till mottagare.',
    kind: 'workflow',
    color: null,
    order: 0,
    pinned: true,
    child_folders: [],
    child_bookmarks: [
      {
        name: 'Mina utkast',
        kind: 'workflow',
        color: workflowPresetColors.attention,
        order: 0,
        pinned: true,
        path: '/dashboard/send-outs',
        query_params: {
          filter: `%7B%22created_by%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22custom_sendout_status_filter%22%3A%7B%22noop%22%3A%5B%22draft%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%7D`,
          model_name: 'sendout',
          _search: '',
        },
      },
      {
        name: 'Mina schemalagda',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 1,
        pinned: true,
        path: '/dashboard/send-outs',
        query_params: {
          filter: `%7B%22created_by%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22custom_sendout_status_filter%22%3A%7B%22noop%22%3A%5B%22scheduled%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%7D`,
          model_name: 'sendout',
          _search: '',
        },
      },
      {
        name: 'Alla schemalagda',
        kind: 'workflow',
        color: workflowPresetColors.infoAlt,
        order: 2,
        pinned: true,
        path: '/dashboard/send-outs',
        query_params: {
          filter:
            '%7B%22custom_sendout_status_filter%22%3A%7B%22noop%22%3A%5B%22scheduled%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'sendout',
          _search: '',
        },
      },
      {
        name: 'Mina skickade',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 3,
        pinned: true,
        path: '/dashboard/send-outs',
        query_params: {
          filter: `%7B%22created_by%22%3A%7B%22noop%22%3A%5B%22${CURRENT_USER_FILTER_SENTINEL}%22%5D%7D%2C%22custom_sendout_status_filter%22%3A%7B%22noop%22%3A%5B%22sent%22%5D%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%7D`,
          model_name: 'sendout',
          _search: '',
        },
      },
      {
        name: 'Alla skickade (senaste 30 dagarna)',
        kind: 'workflow',
        color: workflowPresetColors.active,
        order: 4,
        pinned: true,
        path: '/dashboard/send-outs',
        query_params: {
          filter:
            '%7B%22custom_sendout_status_filter%22%3A%7B%22noop%22%3A%5B%22sent%22%5D%7D%2C%22created_at%22%3A%7B%22__date_time_past%22%3A%2230%22%7D%2C%22is_template%22%3A%7B%22noop%22%3Afalse%7D%7D',
          model_name: 'sendout',
          _search: '',
        },
      },
    ],
  },
];
